import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNotification } from '../../context/NotificationContext';
import axios from 'axios';
import { API_URL } from '../../config/api';
import { getFullImageUrl } from '../../utils/imageUtils';
import MenuItemModal from './MenuItemModal';
import './MenuManagement.css';

const MenuManagement = ({ restaurant, onClose, onUpdate }) => {
  const { user } = useAuth();
  const { showError } = useNotification();
  const getBaseUrl = () => {
    return (user?.role === 'restaurant_owner' || user?.role === 'restaurant_user')
      ? `${API_URL}/restaurant-owner/restaurants/${restaurant._id}`
      : `${API_URL}/admin/restaurants/${restaurant._id}`;
  };
  const getTemplatesUrl = () => {
    return (user?.role === 'restaurant_owner' || user?.role === 'restaurant_user')
      ? `${API_URL}/restaurant-owner/variation-templates`
      : `${API_URL}/admin/variation-templates`;
  };
  const [editingItem, setEditingItem] = useState(null);
  const [newCategory, setNewCategory] = useState('');
  const [showCategoryInput, setShowCategoryInput] = useState(false);
  const [variationTemplates, setVariationTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);

  const fetchVariationTemplates = useCallback(async () => {
    setTemplatesLoading(true);
    try {
      const res = await axios.get(getTemplatesUrl(), { headers: { Accept: 'application/json' } });
      setVariationTemplates(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Failed to load variation templates:', err?.response?.status, err?.response?.data);
      showError(err?.response?.data?.message || 'Could not load variation templates');
    } finally {
      setTemplatesLoading(false);
    }
  }, [user?.role, showError]);

  useEffect(() => {
    if (user?.role) fetchVariationTemplates();
  }, [user?.role, fetchVariationTemplates]);

  useEffect(() => {
    if (editingItem != null && user?.role) {
      fetchVariationTemplates();
    }
  }, [editingItem, user?.role, fetchVariationTemplates]);

  // Default categories
  const defaultCategories = ['Salads', 'Pizzas', 'Burgers', 'Side Dish', 'Drinks', 'Dips', 'Other'];
  const hiddenDefaults = restaurant.hiddenMenuCategories || [];

  // Visible default categories + custom categories, remove duplicates
  const allCategories = [...new Set([
    ...defaultCategories.filter(c => !hiddenDefaults.includes(c)),
    ...(restaurant.menuCategories || [])
  ])];

  const handleDeleteItem = async (itemId) => {
    if (!itemId) {
      showError('Cannot delete: menu item id missing');
      return;
    }
    if (!window.confirm('Are you sure you want to delete this menu item?')) return;

    try {
      const baseUrl = getBaseUrl();
      await axios.delete(`${baseUrl}/menu/${itemId}`);
      onUpdate();
    } catch (error) {
      console.error('Error deleting menu item:', error);
      const message = error.response?.data?.message || error.message || 'Error deleting menu item';
      showError(message);
    }
  };

  const handleSaveItem = async (itemData) => {
    try {
      const baseUrl = getBaseUrl();
      if (editingItem && editingItem._id) {
        await axios.patch(
          `${baseUrl}/menu/${editingItem._id}`,
          itemData
        );
      } else {
        await axios.post(
          `${baseUrl}/menu`,
          itemData
        );
      }
      onUpdate();
      setEditingItem(null);
    } catch (error) {
      console.error('Error saving menu item:', error);
      showError('Error saving menu item');
    }
  };

  const handleAddCategory = async () => {
    if (!newCategory.trim()) return;
    
    const category = newCategory.trim();
    if (allCategories.includes(category)) {
      showError('Category already exists');
      return;
    }

    try {
      const baseUrl = getBaseUrl();
      const updatedCategories = [...(restaurant.menuCategories || []), category];
      await axios.patch(baseUrl, {
        menuCategories: updatedCategories
      });
      setNewCategory('');
      setShowCategoryInput(false);
      onUpdate();
    } catch (error) {
      console.error('Error adding category:', error);
      showError('Error adding category');
    }
  };

  const handleRemoveCategory = async (category) => {
    const isDefault = defaultCategories.includes(category);
    const itemsInCategory = (restaurant.menu || []).filter(item => item.category === category);
    if (itemsInCategory.length > 0) {
      const confirmMessage = `Warning: There are ${itemsInCategory.length} menu item(s) in the category "${category}".\n\nRemoving this category will not delete the items, but they will need to be assigned to a different category.\n\nDo you still want to remove this category?`;
      if (!window.confirm(confirmMessage)) return;
    } else {
      if (!window.confirm(`Are you sure you want to remove the category "${category}"?`)) return;
    }

    try {
      const baseUrl = getBaseUrl();
      if (isDefault) {
        const updatedHidden = [...(restaurant.hiddenMenuCategories || []), category];
        await axios.patch(baseUrl, { hiddenMenuCategories: updatedHidden });
      } else {
        const updatedCategories = (restaurant.menuCategories || []).filter(cat => cat !== category);
        await axios.patch(baseUrl, { menuCategories: updatedCategories });
      }
      onUpdate();
    } catch (error) {
      console.error('Error removing category:', error);
      showError('Error removing category');
    }
  };

  // Group menu items by category
  const menuByCategory = {};
  (restaurant.menu || []).forEach(item => {
    const category = item.category || 'Other';
    if (!menuByCategory[category]) {
      menuByCategory[category] = [];
    }
    menuByCategory[category].push(item);
  });

  return (
    <div className="menu-management-overlay">
      <div className="menu-management-content">
        <div className="menu-management-header">
          <h2>Manage Menu - {restaurant.name}</h2>
          <div className="menu-management-header-actions">
            <label className="track-stock-toggle">
              <input
                type="checkbox"
                checked={!!restaurant.settings?.trackStock}
                onChange={async (e) => {
                  try {
                    const baseUrl = getBaseUrl().replace(/\/menu.*$/, '');
                    await axios.patch(baseUrl, { settings: { ...(restaurant.settings || {}), trackStock: e.target.checked } });
                    onUpdate();
                  } catch (err) {
                    showError(err?.response?.data?.message || 'Failed to update setting');
                  }
                }}
              />
              <span>Track stock</span>
            </label>
            <button className="close-btn" onClick={onClose}>×</button>
          </div>
        </div>

        <div className="menu-management-body">
          {/* Category Management Section */}
          <div className="category-management-section">
            <div className="section-header">
              <h3>Menu Categories</h3>
              <button 
                className="btn-add-category"
                onClick={() => setShowCategoryInput(!showCategoryInput)}
              >
                <i className="fas fa-plus"></i>
                Add Category
              </button>
            </div>
            
            {showCategoryInput && (
              <div className="add-category-form">
                <input
                  type="text"
                  placeholder="Enter category name"
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddCategory()}
                />
                <button onClick={handleAddCategory} className="btn-save-category">
                  <i className="fas fa-check"></i>
                </button>
                <button onClick={() => { setShowCategoryInput(false); setNewCategory(''); }} className="btn-cancel-category">
                  <i className="fas fa-times"></i>
                </button>
              </div>
            )}

            <div className="categories-list">
              {allCategories.map((category, index) => {
                const itemsInCategory = (restaurant.menu || []).filter(item => item.category === category).length;
                const isDefault = defaultCategories.includes(category);
                return (
                  <div key={index} className={`category-tag ${isDefault ? 'default-category' : 'custom-category'}`}>
                    <div className="category-info">
                      <span className="category-name">{category}</span>
                      <span className="category-item-count">({itemsInCategory} item{itemsInCategory !== 1 ? 's' : ''})</span>
                    </div>
                    <button 
                      onClick={() => handleRemoveCategory(category)}
                      className="btn-remove-category"
                      title={`Remove category "${category}"${itemsInCategory > 0 ? ` (${itemsInCategory} items will need reassignment)` : ''}`}
                      aria-label={`Remove category ${category}`}
                    >
                      <i className="fas fa-trash"></i>
                    </button>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Menu Items Section */}
          <div className="menu-items-section">
            <div className="section-header">
              <h3>Menu Items</h3>
              <button 
                className="btn-add-item"
                onClick={() => setEditingItem({})}
              >
                <i className="fas fa-plus"></i>
                Add Menu Item
              </button>
            </div>

            {Object.keys(menuByCategory).length === 0 ? (
              <div className="empty-menu-state">
                <i className="fas fa-utensils"></i>
                <p>No menu items yet. Click 'Add Menu Item' to get started.</p>
              </div>
            ) : (
              <div className="menu-items-list">
                {Object.entries(menuByCategory).map(([category, items]) => (
                  <div key={category} className="menu-category-group">
                    <h4 className="category-title">{category}</h4>
                    <div className="menu-items-grid">
                      {items.map(item => (
                        <div key={item._id} className="menu-item-card">
                          <div className="menu-item-image">
                            {item.image ? (
                              <img src={getFullImageUrl(item.image)} alt={item.name} />
                            ) : (
                              <div className="no-image-placeholder">
                                <i className="fas fa-image"></i>
                              </div>
                            )}
                          </div>
                          <div className="menu-item-info">
                            <h5>{item.name}</h5>
                            <p className="menu-item-description">{item.description || 'No description'}</p>
                            <p className="menu-item-price">€{item.price?.toFixed(2) || '0.00'}</p>
                            {restaurant.settings?.trackStock && (
                              <p className="menu-item-stock">Stock: {item.stockQty != null ? item.stockQty : '—'}</p>
                            )}
                            {(item.variations?.length > 0 || item.ingredients?.length > 0) && (
                              <span className="variations-badge">
                                {item.variations?.length > 0 && <><i className="fas fa-list"></i> {item.variations.length} variation{item.variations.length !== 1 ? 's' : ''}</>}
                                {item.ingredients?.length > 0 && <><i className="fas fa-leaf"></i> {item.ingredients.length} ingredient{item.ingredients.length !== 1 ? 's' : ''}</>}
                              </span>
                            )}
                          </div>
                          <div className="menu-item-actions">
                            <button 
                              onClick={() => setEditingItem(item)}
                              className="btn-edit-item"
                              title="Edit"
                            >
                              <i className="fas fa-edit"></i>
                            </button>
                            <button 
                              onClick={() => handleDeleteItem(item._id)}
                              className="btn-delete-item"
                              title="Delete"
                            >
                              <i className="fas fa-trash"></i>
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="menu-management-footer">
          <button onClick={onClose} className="btn-close">Close</button>
        </div>
      </div>

      {editingItem !== null && (
        <MenuItemModal
          isOpen={editingItem !== null}
          onClose={() => setEditingItem(null)}
          onSave={handleSaveItem}
          item={editingItem}
          isProduct={false}
          categories={allCategories}
          restaurantId={restaurant._id}
          variationTemplates={variationTemplates}
          variationTemplatesLoading={templatesLoading}
        />
      )}
    </div>
  );
};

export default MenuManagement;
